import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { trainingAPI } from '../utils/authUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, LogOut, BookOpen, FolderOpen, User } from 'lucide-react';

const TrainingPortal = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [categoriesData, commissionsData] = await Promise.all([
        trainingAPI.getCategories(),
        trainingAPI.getMyCommissions()
      ]);
      setCategories(categoriesData);
      setCommissions(commissionsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data', {
        description: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const totalEarned = commissions
    .filter(c => c.status === 'paid')
    .reduce((sum, c) => sum + c.amount, 0);

  const pendingCommissions = commissions
    .filter(c => c.status === 'pending')
    .reduce((sum, c) => sum + c.amount, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <img src="/qnc-logo.png" alt="QNC" className="h-12 w-auto" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Training Portal</h1>
                <p className="text-sm text-gray-600">Welcome, {user?.full_name}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="outline" size="sm">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                  </svg>
                  Back to Home
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Commission Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card className="border-2 border-green-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <span className="text-2xl mr-2">💰</span>
                Total Earned
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                ${totalEarned.toFixed(2)}
              </div>
              <p className="text-sm text-gray-600 mt-1">Paid commissions</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <span className="text-2xl mr-2">⏳</span>
                Pending
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-yellow-600">
                ${pendingCommissions.toFixed(2)}
              </div>
              <p className="text-sm text-gray-600 mt-1">Awaiting payment</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-100">
            <CardHeader>
              <CardTitle className="flex items-center text-lg">
                <span className="text-2xl mr-2">📊</span>
                Total Sales
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {commissions.length}
              </div>
              <p className="text-sm text-gray-600 mt-1">Confirmed sales</p>
            </CardContent>
          </Card>
        </div>

        {/* Training Categories */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Training Materials</h2>
          {categories.length === 0 ? (
            <Card>
              <CardContent className="pt-6 text-center py-12">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No training materials available yet.</p>
                <p className="text-sm text-gray-500 mt-2">Check back soon for updates!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <Link key={category.id} to={`/training-portal/${category.id}`}>
                  <Card className="hover:shadow-lg transition-shadow duration-300 cursor-pointer border-2 hover:border-blue-300">
                    <CardHeader>
                      <CardTitle className="flex items-center text-xl">
                        <span className="text-3xl mr-3">{category.icon}</span>
                        {category.name}
                      </CardTitle>
                      <CardDescription>{category.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button className="w-full bg-blue-600 hover:bg-blue-700">
                        <FolderOpen className="mr-2 h-4 w-4" />
                        View Materials
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Profile Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="mr-2 h-5 w-5" />
              My Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-semibold">{user?.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="font-semibold">{user?.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <p className="font-semibold">{user?.phone || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Location</p>
                <p className="font-semibold">{user?.location || 'N/A'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TrainingPortal;
